import React from 'react';
import Skeleton from '@mui/material/Skeleton';
import { Collapse, Grid } from '@mui/material';

import { IntlMessage } from 'app/components/Intl';
import { DetailsPanelRowValueWithReset } from 'app/components/DetailsPanelRow/DetailsPanelRowValueWithReset';

import { RegulationsFieldInput } from '../Fields/RegulationsFieldInput';
import { RegulationsFieldDisplay } from '../Fields/RegulationsFieldDisplay';
import type { RegulationsPanelRow as RegulationsPanelRowType } from '../Fields/RegulationsField.types';

import {
  ListItemStyled,
  ListStyled,
  SpanFieldLabelStyled,
  SpanFiledValueStyled,
  TypographyColumnTitleStyled,
} from './RegulationsFieldsPanel.styles';

export const REGULATIONS_FIELDS_PANEL_ROW_DATA_TEST_ID = 'regulations-fields-panel-row';

type RegulationsFieldsPanelRowProps = {
  rows: Array<RegulationsPanelRowType>;
  loading?: boolean;
  disabled?: boolean;
  config?: { skeletonRows?: number };
};

const RegulationsFieldsPanelRow = ({
  rows,
  loading = false,
  disabled = false,
  config,
}: RegulationsFieldsPanelRowProps) => {
  const skeletonRows = config?.skeletonRows || 5;

  const skeletonRowElements = React.useMemo(() => new Array(skeletonRows).fill(null), [skeletonRows]);

  const renderFieldLabel = React.useCallback(
    (row: RegulationsPanelRowType) => {
      if (loading) {
        return <Skeleton width={'100%'} height={'100%'} />;
      }

      const rowValue = row.value;
      const shouldRenderInputLabel = rowValue?.editable && !!rowValue?.label;
      const isLabelHighlighted = rowValue?.label?.highlight || false;

      if (shouldRenderInputLabel && rowValue.label) {
        return (
          <RegulationsFieldInput
            field={{
              type: 'text',
              name: rowValue.label.name,
              defaultValue: rowValue.label.defaultValue,
              variant: rowValue.label.variant,
              required: rowValue.label.required,
              disabled: rowValue.label.disabled || disabled,
            }}
          />
        );
      }

      const label = (
        <SpanFieldLabelStyled disabled={disabled} highlighted={isLabelHighlighted}>
          <IntlMessage value={row.key} />
        </SpanFieldLabelStyled>
      );

      if (rowValue?.label?.onReset) {
        return <DetailsPanelRowValueWithReset value={label} onReset={rowValue.label.onReset} />;
      }

      return label;
    },
    [disabled, loading],
  );

  const renderValue = React.useCallback(
    (row: RegulationsPanelRowType) => {
      if (loading) {
        return <Skeleton width={'100%'} height={'100%'} />;
      }

      const rowValue = row.value;
      const field = { ...rowValue.field, disabled: rowValue.field.disabled || disabled };
      const isValueResettable = !!field.onReset;
      const isValueHighlighted = field.highlight || false;

      const valueContent =
        rowValue.editable === true ? (
          <RegulationsFieldInput field={field} />
        ) : (
          <SpanFiledValueStyled disabled={field.disabled} highlighted={isValueHighlighted}>
            <RegulationsFieldDisplay field={field} />
          </SpanFiledValueStyled>
        );

      if (isValueResettable && field.onReset) {
        return <DetailsPanelRowValueWithReset value={valueContent} onReset={field.onReset} />;
      }

      return valueContent;
    },
    [disabled, loading],
  );

  const renderedRows = React.useMemo(() => {
    let rowsToRender: Array<RegulationsPanelRowType | null> = rows;
    if (rowsToRender.length === 0 && loading) {
      rowsToRender = skeletonRowElements;
    }

    const renderRowContent = (row: RegulationsPanelRowType | null, index: number) => {
      // Table fields span the full row width - like the legacy panels, they never get a label column.
      const isTableRow = row?.value?.field?.type === 'table';
      const hasKey = !isTableRow && !!row?.key;
      const label = row ? renderFieldLabel(row) : <Skeleton width={'100%'} height={'100%'} />;
      const value = row ? renderValue(row) : <Skeleton width={'100%'} height={'100%'} />;

      return (
        <ListItemStyled key={row?.value?.name ?? index}>
          <Grid container spacing={1}>
            {hasKey && (
              <Grid item xs={12} sm={5}>
                {label}
              </Grid>
            )}
            <Grid item xs={12} sm={hasKey ? 7 : 12}>
              {value}
            </Grid>
          </Grid>
        </ListItemStyled>
      );
    };

    const groupedRows = rowsToRender.reduce<Record<string, Array<RegulationsPanelRowType | null>>>((acc, row) => {
      const groupName = row?.groupName || '';
      acc[groupName] = acc[groupName] || [];
      acc[groupName].push(row);
      return acc;
    }, {});

    // Table fields are wide/tall, so they're rendered first within their group rather than wherever
    // they happen to sort alphabetically among the plain key/value rows.
    const isTableRow = (row: RegulationsPanelRowType | null) => row?.value?.field?.type === 'table';
    Object.keys(groupedRows).forEach(groupName => {
      groupedRows[groupName] = [
        ...groupedRows[groupName].filter(isTableRow),
        ...groupedRows[groupName].filter(row => !isTableRow(row)),
      ];
    });

    const renderedGroups: React.ReactNode[] = [];

    if (groupedRows['']) {
      renderedGroups.push(...groupedRows[''].map((row, index) => renderRowContent(row, index)));
      delete groupedRows[''];
    }

    Object.entries(groupedRows).forEach(([groupName, rowsInGroup]) => {
      renderedGroups.push(
        <React.Fragment key={groupName}>
          <TypographyColumnTitleStyled variant={'subtitle2'} withMarginTop withMarginBottom={false}>
            <IntlMessage value={groupName} />
          </TypographyColumnTitleStyled>
          {rowsInGroup.map((row, index) => renderRowContent(row, index))}
        </React.Fragment>,
      );
    });

    return renderedGroups;
  }, [rows, loading, skeletonRowElements, renderFieldLabel, renderValue]);

  return (
    <ListStyled data-testid={REGULATIONS_FIELDS_PANEL_ROW_DATA_TEST_ID}>
      <Collapse in>{renderedRows}</Collapse>
    </ListStyled>
  );
};

export default React.memo(RegulationsFieldsPanelRow);
